import { useEffect, useMemo, useState } from 'react';
import {
    PiArrowsDownUpDuotone,
    PiArrowUpDuotone
} from 'react-icons/pi';

import {
    prepareSidebarTableRows,
    getSidebarTableCellDisplayValue
} from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';

const COLOR_CLASS_MAP = {
    green: {
        row: 'bg-green-50',
        cell: 'text-green-700'
    },
    red: {
        row: 'bg-red-50',
        cell: 'text-red-700'
    },
    yellow: {
        row: 'bg-yellow-50',
        cell: 'text-yellow-700'
    }
};

const ALIGN_CLASS_MAP = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
};

function useDebouncedValue(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timeout = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(timeout); };
    }, [value, delay]);

    return debouncedValue;
}

function getCell(row, columnKey) {
    return row?.cells?.[columnKey] || null;
}

function StateView(props) {
    const { title, description } = props;

    return (
        <div className='flex-1 flex flex-col items-center justify-center gap-2 px-16 py-16 text-center' style={{ minHeight: 220 }}>

            <div className='text-xs opacity-50'>{title}</div>

            {description ? (<div className='max-w-md text-xs opacity-30'>{description}</div>) : null}

        </div>
    );
}

export default function OptimizationPipelineSidebarTable(props) {
    const {
        tableConfig,
        rows = [],
        loading = false,
        error = null,
        query: controlledQuery,
        onQueryChange,
        sort: controlledSort,
        onSortChange
    } = props;

    const { columns = [], search = {}, sort = {}, states = {} } = tableConfig || {};

    const { enabled: searchEnabled = true, showInput = true, debounceMs = 250, placeholder = 'Search...' } = search;
    const { key: defaultSortKey = null, direction: defaultSortDirection = 'asc' } = sort;

    const [internalQuery, setInternalQuery] = useState(controlledQuery || '');
    const [internalSort, setInternalSort] = useState({ key: defaultSortKey, direction: defaultSortDirection });

    useEffect(() => {
        setInternalSort({ key: defaultSortKey, direction: defaultSortDirection });
    }, [defaultSortKey, defaultSortDirection]);

    useEffect(() => {
        if (controlledQuery !== undefined) { setInternalQuery(controlledQuery); }
    }, [controlledQuery]);

    const currentQuery = controlledQuery !== undefined ? controlledQuery : internalQuery;
    const activeSort = controlledSort || internalSort;
    const debouncedQuery = useDebouncedValue(currentQuery, debounceMs);

    const preparedRows = useMemo(() => {
        return prepareSidebarTableRows(tableConfig, rows, debouncedQuery, activeSort);
    }, [activeSort, debouncedQuery, rows, tableConfig]);

    const handleQueryChange = (event) => {
        const nextQuery = event.target.value;

        if (controlledQuery === undefined) {
            setInternalQuery(nextQuery);
        }

        if (typeof onQueryChange === 'function') {
            onQueryChange(nextQuery);
        }
    };

    const handleSort = (column) => {
        if (!column.sortable) { return; }

        const nextSort = activeSort?.key !== column.key
            ? { key: column.key, direction: 'asc' }
            : { key: column.key, direction: activeSort.direction === 'asc' ? 'desc' : 'asc' };

        if (typeof onSortChange === 'function') {
            onSortChange(nextSort);
        } else {
            setInternalSort(nextSort);
        }
    };

    const hasRows = rows.length > 0;
    const hasPreparedRows = preparedRows.length > 0;

    return (
        <div className='flex h-full min-h-0 flex-col overflow-hidden'>

            {searchEnabled && showInput ? (
                <div className='border-b border-grey-200 p-3'>
                    <input
                        type='text'
                        value={currentQuery}
                        onChange={handleQueryChange}
                        placeholder={placeholder}
                        // className='w-full rounded-xl border border-grey-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-grey-300'
                        // className='h-28 px-12 rounded-lg border text-11 font-medium'
                    />
                </div>
            ) : null}

            <div className='min-h-0 flex-1 flex flex-col overflow-auto'>

                {loading ? (
                    <StateView
                        title={states.loadingTitle || 'Loading...'}
                        description={states.loadingDescription || ''}
                    />
                ) : error ? (
                    <StateView
                        title={states.errorTitle || 'Something went wrong'}
                        description={states.errorDescription || 'The table data could not be loaded.'}
                    />
                ) : !hasRows ? (
                    <StateView
                        title={states.emptyTitle || 'No data available'}
                        description={states.emptyDescription || ''}
                    />
                ) : !hasPreparedRows ? (
                    <StateView
                        title={states.noResultsTitle || 'No matching results'}
                        description={states.noResultsDescription || ''}
                    />
                ) : (
                    <table className='w-full border-collapse table-fixed'>

                        <thead className='sticky top-0 z-10 bg-grey-50'>
                            <tr className='border-b border-grey-200'>
                                {columns.map((column) => {
                                    const alignClassName = ALIGN_CLASS_MAP[column.align || 'left'] || ALIGN_CLASS_MAP.left;
                                    const isActiveSortColumn = activeSort?.key === column.key;

                                    return (
                                        <th
                                            key={column.key}
                                            onClick={() => handleSort(column)}
                                            className={
                                                'group border-b border-grey-200 px-16 py-16 text-xs font-medium tracking-wide ' +
                                                alignClassName + ' ' +
                                                (column.sortable ? 'cursor-pointer select-none' : '') + ' ' +
                                                (column.className || '')
                                            }
                                            style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                                            title={column.sortable ? 'Sort' : undefined}
                                        >
                                            <div className={'flex items-center gap-6 ' + (column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : 'justify-start')}>
                                                <span className='truncate'>{column.label}</span>

                                                {column.sortable ? (
                                                    <span
                                                        className={
                                                            'flex flex-shrink-0 items-center justify-center text-sm transition-all duration-200 ' +
                                                            (isActiveSortColumn ? '' : 'opacity-50')
                                                        }
                                                    >
                                                        {isActiveSortColumn ? (
                                                            <PiArrowUpDuotone
                                                                className={
                                                                    'transform transition-all duration-200 scale-110 ' +
                                                                    (activeSort.direction === 'desc' ? 'rotate-180' : 'rotate-0')
                                                                }
                                                            />
                                                        ) : (
                                                            <PiArrowsDownUpDuotone className='transition-opacity duration-200 opacity-50 group-hover:opacity-100' />
                                                        )}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>

                        <tbody>
                            {preparedRows.map((row, index) => {
                                const stripedClassName = index % 2 === 1 ? 'bg-grey-50' : '';
                                const rowColorClassName = row?.rowColor ? COLOR_CLASS_MAP[row.rowColor]?.row || '' : '';

                                return (
                                    <tr
                                        key={row.id}
                                        className={'border-b border-dashed border-grey-200 transition ' + stripedClassName + ' ' + rowColorClassName}
                                    >
                                        {columns.map((column) => {
                                            const cell = getCell(row, column.key);
                                            const alignClassName = ALIGN_CLASS_MAP[column.align || 'left'] || ALIGN_CLASS_MAP.left;
                                            const cellColorClassName = cell?.color ? COLOR_CLASS_MAP[cell.color]?.cell || '' : '';
                                            const displayValue = getSidebarTableCellDisplayValue(row, column);
                                            const shouldTruncate = column.truncate && !cell?.component;
                                            const title = column.tooltip ? (cell?.tooltip || String(displayValue)) : undefined;

                                            return (
                                                <td
                                                    key={column.key}
                                                    className={
                                                        'px-16 py-16 text-xs text-grey-700 ' +
                                                        alignClassName + ' ' +
                                                        cellColorClassName + ' ' +
                                                        (column.numeric ? 'tabular-nums' : '') + ' ' +
                                                        (column.cellClassName || '')
                                                    }
                                                    style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                                                    title={title}
                                                >
                                                    {cell?.component ? (
                                                        cell.component
                                                    ) : shouldTruncate ? (
                                                        <div className='w-full truncate'>{displayValue}</div>
                                                    ) : (
                                                        displayValue
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>

                    </table>
                )}

            </div>

        </div>
    );
}

/*
EXAMPLE TABLE CONFIG

const unitMeasurementTableConfig = {
    columns: [
        {
            key: 'attribute',
            label: 'Attribute',
            sortable: true,
            searchable: true,
            align: 'left',
            minWidth: 180,
            maxWidth: 260,
            truncate: true,
            tooltip: true
        },
        {
            key: 'value',
            label: 'Value',
            sortable: true,
            searchable: true,
            align: 'right',
            minWidth: 120,
            maxWidth: 140,
            numeric: true,
            format: (value) => value
        },
        {
            key: 'uom',
            label: 'Unit',
            sortable: true,
            searchable: true,
            align: 'left',
            minWidth: 80,
            maxWidth: 100
        }
    ],
    search: {
        enabled: true,
        showInput: false,
        debounceMs: 250,
        placeholder: 'Search measurements...'
    },
    sort: {
        key: 'attribute',
        direction: 'asc'
    },
    states: {
        loadingTitle: 'Loading measurements...',
        errorTitle: 'Failed to load measurements',
        errorDescription: 'Please try again later.',
        emptyTitle: 'No measurements available',
        noResultsTitle: 'No matching measurements'
    }
};

EXAMPLE ROW DATA

const unitMeasurementRows = [
    {
        id: 'temperature',
        rowColor: 'green',
        searchableText: 'temperature 123.46 celsius',
        cells: {
            attribute: {
                value: 'Temperature'
            },
            value: {
                value: 123.456,
                displayValue: '123.46',
                color: 'green'
            },
            uom: {
                value: '°C'
            }
        }
    },
    {
        id: 'pressure',
        rowColor: 'yellow',
        cells: {
            attribute: {
                value: 'Pressure',
                tooltip: 'Main line pressure'
            },
            value: {
                value: 5.2,
                displayValue: '5.20',
                color: 'yellow'
            },
            uom: {
                value: 'bar'
            }
        }
    }
];

EXAMPLE CUSTOM COMPONENT CELL

const optimizationDetailsRows = [
    {
        id: 'optimizer-trend',
        cells: {
            attribute: {
                value: 'Optimizer trend'
            },
            chart: {
                component: <MiniChart data={chartData} />
            }
        }
    }
];
*/